/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/PromptView.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { MessageSquareCode, Copy, Info } from 'lucide-react';
import { api } from '../lib/api';
import { useApp } from '../lib/store';
import { dateTime } from '../lib/format';
import { userName } from '../lib/users';
import { I, Badge, Modal, ErrorCard, LoadingRows } from './ui';
import '../styles/instructions.css';

/** GET /api/t/:tid/documents/:did/prompt: what the extractor sends for this Beleg (ADR-026). */
interface PromptData {
  documentId: string; filename: string; mode: 'claude' | 'offline'; model: string; system: string; context: string;
  document: { type: 'document' | 'image' | 'text'; mediaType: string; bytes?: number; chars?: number };
  tool: { name: string; choice: string; fields: string[]; customFields: string[] };
  instructions: { sha256: string; short: string; versionId: string | null; status: string | null; author: string | null; approvedBy: string | null; approvedAt: string | null; chars: number | null; capped: boolean } | null;
  readWith: { engine: string; instructionSha: string | null } | null;
  notes: string[];
}

const person = (id: string | null) => (id === 'migration' ? 'Migration' : userName(id));
const size = (b = 0) => (b < 1024 ? `${b} B` : `${(b / 1024).toFixed(1)} KB`);
const block = (d: PromptData['document']) => d.type === 'document' ? `PDF als document-Block, ${size(d.bytes)}` : d.type === 'image' ? `Bild (${d.mediaType}) als image-Block, ${size(d.bytes)}` : `Text des Belegs, ${d.chars} Zeichen`;

/** The context with the Arbeitsanweisung block highlighted, so the reader finds what the fiduciary wrote. */
function Context({ text }: { text: string }) {
  const a = text.indexOf('\nMandantenspezifische Anweisungen');
  const end = text.indexOf('</anweisungen>', a);
  if (a < 0 || end < 0) return <>{text}</>;
  const b = end + '</anweisungen>'.length;
  return <>{text.slice(0, a + 1)}<span className="prompt-hl">{text.slice(a + 1, b)}</span>{text.slice(b)}</>;
}

/** «Prompt ansehen»: a button that opens the read-only, copyable view of the extraction request. */
export function PromptButton({ tid, documentId, className = 'btn sm' }: { tid: string; documentId: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button className={className} onClick={() => setOpen(true)} aria-label="Prompt ansehen" title="Zeigt, was Kontor für diesen Beleg an das Modell schickt"><I icon={MessageSquareCode} sm /><span className="pv-label">Prompt ansehen</span></button>
      {open && <PromptModal tid={tid} documentId={documentId} onClose={close} />}
    </>
  );
}

function PromptModal({ tid, documentId, onClose }: { tid: string; documentId: string; onClose: () => void }) {
  const app = useApp();
  const [data, setData] = useState<PromptData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    api.get<PromptData>(`/api/t/${tid}/documents/${documentId}/prompt`).then(setData).catch((e) => setError(e.message));
  }, [tid, documentId]);
  useEffect(load, [load]);
  // Opened from a drawer: Esc closes this dialog only. The capture phase runs before the drawer's handler.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onClose]);

  const copyAll = async () => {
    if (!data) return;
    const all = [`Modell: ${data.model}`, `System:\n${data.system}`, `Beleg: ${block(data.document)}`, `Text:\n${data.context}`,
      `Tool: ${data.tool.name} (erzwungen), Felder: ${data.tool.fields.join(', ')}`].join('\n\n');
    try { await navigator.clipboard.writeText(all); app.toast('Prompt kopiert'); } catch { app.toast('Kopieren nicht möglich: Text markieren und kopieren', 'error'); }
  };

  const ins = data?.instructions;
  const rows: [string, ReactNode][] = data ? [
    ['Modus', data.mode === 'claude' ? 'Claude' : <>Offline <span className="dim">· so sähe die Anfrage an Claude aus</span></>],
    ['Modell', <span className="mono" data-copy-unit>{data.model}</span>],
    ['Beleg', block(data.document)],
    ['Anweisungen', ins
      ? <span><Badge kind={ins.status === 'active' ? 'pos' : 'warn'}>Version <span className="mono" data-copy-unit data-copy-value={ins.sha256}>{ins.short}</span></Badge>{' '}
          <span className="dim">{ins.approvedBy ? `freigegeben von ${person(ins.approvedBy)}, ${dateTime(ins.approvedAt)}` : 'ohne gespeicherte Freigabe'}{ins.capped ? ' · gekürzt auf 2’000 Zeichen' : ''}</span></span>
      : <span className="dim">keine</span>],
    ['Beim Lesen', data.readWith ? `${data.readWith.engine === 'claude' ? 'Claude' : 'Offline-Heuristik'}${data.readWith.instructionSha ? `, Version ${data.readWith.instructionSha.slice(0, 7)}` : ', ohne Anweisungen'}` : 'noch nicht gelesen'],
    ['Tool', <><span className="mono">{data.tool.name}</span> <span className="dim">· erzwungen</span></>],
    ['Felder', <span className="mono" data-copy-unit>{data.tool.fields.join(', ')}</span>],
    ...(data.tool.customFields.length ? [['Eigene Felder', <span className="mono" data-copy-unit>{data.tool.customFields.join(', ')}</span>] as [string, ReactNode]] : []),
  ] : [];

  return (
    <Modal title={`Prompt für «${data?.filename ?? 'Beleg'}»`} onClose={onClose} className="prompt-modal" footer={<>
      <span className="dim" style={{ fontSize: 'var(--fs-xs)', marginRight: 'auto' }}>Nur lesen · Klick auf einen Block kopiert ihn</span>
      <button className="btn" onClick={copyAll} disabled={!data} data-autofocus><I icon={Copy} sm /> Alles kopieren</button>
      <button className="btn primary" onClick={onClose}>Schliessen</button>
    </>}>
      <div className="prompt-body">
        {error ? <ErrorCard message={error} onRetry={load} /> : !data ? <LoadingRows rows={6} /> : <>
          <table className="prompt-kv"><tbody>{rows.map(([k, v]) => <tr key={k}><td className="k">{k}</td><td>{v}</td></tr>)}</tbody></table>
          <section><h4>System</h4><pre className="prompt-pre mono" data-copy-unit data-copy-value={data.system}>{data.system}</pre></section>
          <section><h4>Text nach dem Beleg</h4><pre className="prompt-pre mono" data-copy-unit data-copy-value={data.context}><Context text={data.context} /></pre></section>
          <ul className="prompt-notes">{data.notes.map((x, i) => <li key={i}><I icon={Info} sm /><span>{x}</span></li>)}</ul>
        </>}
      </div>
    </Modal>
  );
}
